/**
 * The plan builder behind Régénérer, on its own so it can run outside React:
 * `useGenerateWeek` imports react-native through the store, and the plan
 * eval (`npm run eval:plans`) runs this under plain Node.
 */
import { dietKindOf, type DietKind } from '../weekly/v2/dietKind';
import type { PlannedMeal } from '../../redux/mealPlanSlice';
import { isEntryMeal } from '../../redux/mealEntry';
import { rowOf } from '../weekly/v2/constants';
import { dayIndexInWeek } from '../../utils/localDate';
import { isPrimarilyBreakfastRecipe } from '../../utils/recipeClassification';
import { fitsMealType } from './fitsMealType';
import type { RecipeIndexEntry } from '../../services/unifiedRecipeService';
import { SameMealLedger, mealIdentityOf, type DatedMeal } from '../../services/mealIdentity';
import { WeekBalance, narrowForSlot, DISQUALIFYING_SCORE, type PlanSlot } from '../../services/planRanking';

/**
 * One meal per slot of the week, as Régénérer reads it. An entry speaks for
 * its slot: a salad beside « Porcs Sevelin BBQ » must not make the slot look
 * like a recipe dinner to rewrite — `replaceSlotRecipe` would wipe the note
 * along with it. Otherwise the last meal of the slot wins, as before.
 *
 * A meal's column is its date counted from `start`, which is a Monday on the
 * desktop and today on the phone (#808); a meal outside the seven days is
 * not in the map.
 */
export function generationSlotsMap(meals: PlannedMeal[], start: string): Map<number, PlannedMeal> {
  const slotsMap = new Map<number, PlannedMeal>();
  meals.forEach((m) => {
    const di = dayIndexInWeek(start, m.scheduledFor);
    if (di === null) return;
    const mi = ({ breakfast: 0, lunch: 1, dinner: 2 } as const)[m.mealType] ?? 1;
    const current = slotsMap.get(mi * 7 + di);
    if (current && isEntryMeal(current)) return;
    slotsMap.set(mi * 7 + di, m);
  });
  return slotsMap;
}

/** What the generator produced: the picks, plus the slots it honestly could not fill. */
export interface GenerationPlan {
  plan: Record<number, string>;
  /**
   * Selected slots left empty for lack of a suitable recipe (typically breakfast
   * on a library with few morning recipes). Slots preserved because they hold a
   * resto/restes/note entry are NOT listed here — those were never up for grabs.
   */
  unfilled: number[];
  /**
   * Distinct recipes weighed across every slot — the eligible pool each pick
   * drew from, deduplicated. The « Les affinités » footer counts this; it is a
   * real number, never a flourish.
   */
  reviewed: number;
}


export interface GenerationOptions {
  /** Per-slot overrides from the config UI (kind: 'auto' | DietKind). */
  perCell?: Record<number, { portions?: number; kind?: string }>;
  /** Soft weekly distribution targets — a guide the picks approach, never a hard constraint. */
  targets?: { fish: number; veg: number; meat: number };
  /** Recipe classifier (injected for testability). */
  kindOf?: (r: RecipeIndexEntry) => DietKind;
  /**
   * Preference score per recipe; when set, picks come from the best-scoring
   * third of the candidates. `sk` is the slot being filled, so a weeknight and
   * a Saturday can want different things out of the same library.
   */
  prefer?: (r: RecipeIndexEntry, sk: number) => number;
  /**
   * Hard exclusion — allergies, intolerances, excluded ingredients, declared
   * diets. Unlike diet kind, this never gives way: a slot with nothing safe
   * left to serve is reported in `unfilled` and stays empty.
   */
  exclude?: (r: RecipeIndexEntry) => boolean;
  /**
   * The same-meal rules (#736): dinners around the slots that the picks must
   * not repeat, and the calendar day each slot falls on. Without it the only
   * variety this builder had was "not the same id twice in one run".
   */
  sameMeal?: { history: DatedMeal[]; dateOf: (sk: number) => string };
  /**
   * What the cook's schedule asks of a dinner slot (`planSlotFor`), or null
   * for a slot it does not describe. A vegetarian or fish day becomes that
   * cell's diet unless the config screen set one; its time cap and the week's
   * protein balance narrow the picks the way they do in every generator.
   */
  slotOf?: (sk: number) => PlanSlot | null;
}

/**
 * Pure plan builder — exported for tests. Fills only slots that are empty or
 * hold a recipe; slots holding a non-recipe entry are preserved (skipped).
 *
 * Diet-aware (design spec 'Configurer ma semaine'): slots with an explicit
 * kind are served first from that kind's pool; the picked kinds consume the
 * remaining soft targets; 'auto' slots then aim at the kind with the highest
 * remaining target. Diet is a SOFT constraint — it never prevents a slot from
 * being filled.
 *
 * Meal type, by contrast, is a HARD constraint (#528): a slot the library has no
 * suitable recipe for is left empty and reported in `unfilled`, never plugged
 * with a dinner dish.
 */
export function buildGenerationPlan(
  selections: number[],
  recipeIndex: RecipeIndexEntry[],
  slotsMap: Map<number, PlannedMeal>,
  opts?: GenerationOptions,
): GenerationPlan {
  const perCell = opts?.perCell ?? {};
  const kindOf = opts?.kindOf ?? dietKindOf;
  const prefer = opts?.prefer;
  const exclude = opts?.exclude;
  const remaining = { ...(opts?.targets ?? { fish: 0, veg: 0, meat: 0 }) };

  const plan: Record<number, string> = {};
  const unfilled: number[] = [];
  const usedIds = new Set<string>();
  const reviewedIds = new Set<string>();
  const sameMeal = opts?.sameMeal;
  const ledger = new SameMealLedger(sameMeal?.history);

  const fillable = selections.filter((sk) => {
    const current = slotsMap.get(sk);
    return !(current && isEntryMeal(current)); // préserve resto / note / restes / rien
  });

  const pickFor = (sk: number, want: DietKind | 'auto'): RecipeIndexEntry | undefined => {
    const eligible = recipeIndex.filter(
      (r) => !usedIds.has(r.id) && fitsMealType(r, rowOf(sk)) && !(exclude && exclude(r)),
    );
    eligible.forEach((r) => reviewedIds.add(r.id));
    // Real morning dishes before ones merely tagged "breakfast, dinner, …".
    const primary = rowOf(sk) === 0 ? eligible.filter(isPrimarilyBreakfastRecipe) : eligible;
    const pool = primary.length > 0 ? primary : eligible;
    const wanted = pool.filter((r) => want === 'auto' || kindOf(r) === want);
    const byDiet = wanted.length > 0 ? wanted : pool; // diet gives way, meal type never does
    // Never the same meal within the windows; the looser rules give way first.
    const allowed = sameMeal
      ? ledger.allowed(byDiet, mealIdentityOf, sameMeal.dateOf(sk)).kept
      : byDiet;
    // What the cook's taste rules out (the opposite heat, a long dish on a
    // quick night) goes before the week's balance is even asked.
    const scores = new Map(prefer ? allowed.map((r) => [r, prefer(r, sk)] as const) : []);
    const viable = prefer ? allowed.filter((r) => scores.get(r)! > DISQUALIFYING_SCORE) : allowed;
    const slot = opts?.slotOf?.(sk);
    const tasteful = viable.length > 0 ? viable : allowed;
    // The schedule's kind is held here only when the config screen did not set
    // one; an explicit cell kind already chose the pool above.
    const scheduledKind = !!slot && !isExplicit(sk);
    const candidates = slot ? narrowForSlot(tasteful, (r) => r, slot, balance, { kind: scheduledKind }) : tasteful;
    if (candidates.length === 0) return undefined;
    if (!prefer) return candidates[Math.floor(Math.random() * candidates.length)];
    const ranked = candidates
      .map((r) => ({ r, s: scores.get(r)! }))
      .sort((a, b) => b.s - a.s);
    const top = ranked.slice(0, Math.max(3, Math.ceil(ranked.length * 0.3)));
    return top[Math.floor(Math.random() * top.length)].r;
  };

  const place = (sk: number, want: DietKind | 'auto') => {
    const pick = pickFor(sk, want);
    if (!pick) {
      unfilled.push(sk);
      return;
    }
    plan[sk] = pick.id;
    usedIds.add(pick.id);
    if (sameMeal) ledger.add(sameMeal.dateOf(sk), mealIdentityOf(pick));
    const slot = opts?.slotOf?.(sk);
    if (slot) balance.add(pick, slot);
    const kind = kindOf(pick);
    if (remaining[kind] > 0) remaining[kind]--;
  };

  const SCHEDULED: Partial<Record<string, DietKind>> = { vegetarian: 'veg', fish: 'fish', meat: 'meat' };
  function isExplicit(sk: number): boolean {
    const kind = perCell[sk]?.kind;
    return kind === 'fish' || kind === 'veg' || kind === 'meat';
  }
  const cellKind = (sk: number): DietKind | 'auto' => {
    if (isExplicit(sk)) return perCell[sk]!.kind as DietKind;
    return SCHEDULED[opts?.slotOf?.(sk)?.kind ?? ''] ?? 'auto';
  };

  // The fish days asked for, by the schedule or the config screen, are never
  // held against the week's fish cap.
  const balance = new WeekBalance(
    fillable.filter((sk) => opts?.slotOf?.(sk) && cellKind(sk) === 'fish').length,
  );

  // A dinner that stays (tonight's, #862) is part of the week it balances. No
  // slot passed: it is not one of the fish days reserved above.
  const recipeById = new Map(recipeIndex.map((r) => [r.id, r]));
  slotsMap.forEach((meal, sk) => {
    const recipe = recipeById.get(meal.recipeId);
    if (recipe && opts?.slotOf?.(sk) && !selections.includes(sk)) balance.add(recipe);
  });

  // 1. Explicit kinds first — they also consume the soft targets.
  fillable.filter((sk) => cellKind(sk) !== 'auto').forEach((sk) => place(sk, cellKind(sk)));

  // 2. Auto slots aim at the kind with the highest remaining target.
  fillable.filter((sk) => cellKind(sk) === 'auto').forEach((sk) => {
    const [topKind, topCount] = (Object.entries(remaining) as [DietKind, number][])
      .sort((a, b) => b[1] - a[1])[0];
    place(sk, topCount > 0 ? topKind : 'auto');
  });

  return { plan, unfilled: unfilled.sort((a, b) => a - b), reviewed: reviewedIds.size };
}
